import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import AddDropdownHeader from '@components/AddDropdownHeader';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import PublicLinkField from '@components/PublicLinkField';
import { useLocations, useTranslation } from '@hooks';
import { RoutePath } from '@constants/routes';
import { InfoOutlinedIcon } from 'assets/images/icons';
import { getDocumentLanguages, areOptionsEnabled } from '../helpers';

const toPreviewNumber = value => (value === null || value === undefined || value === '' ? null : Number(value));

const Preview = ({ defaultLanguage, isCreate }) => {
    const { watch } = useFormContext();
    const { t } = useTranslation();
    const { locationsMap } = useLocations();
    const brands = useSelector(state => state.brands.data);
    const accountLocale = useSelector(state => state.account?.account?.locale);
    const iframeRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const menuId = watch('id');
    const document = watch('document');
    const currencyCode = watch('currencyCode');
    const locationIds = watch('locationIds');

    const languages = useMemo(() => getDocumentLanguages(document), [document]);

    // Emulates opening /m/{menuId}:{locationId} for the chosen assigned location;
    // defaults to the first one while none is picked (or the pick was unassigned).
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const effectiveLocationId = (locationIds || []).includes(selectedLocationId)
        ? selectedLocationId
        : (locationIds?.[0] ?? null);

    const getLocationLabel = id => locationsMap[id]?.internalName || locationsMap[id]?.locationName || id;

    const publicLink = `${import.meta.env.REACT_APP_SURVEY_DOMAIN}/m/${menuId}${
        effectiveLocationId ? `:${effectiveLocationId}` : ''
    }`;

    const getPreviewData = () => {
        const location = effectiveLocationId ? locationsMap[effectiveLocationId] : null;

        let appearance = null;
        if (location) {
            appearance = location.brand ? brands?.find(b => b.id === location.brand.id)?.appearance || null : null;
        } else {
            // Mirror GetMenuPublicCommand.SetBranding: branding applies when the assigned
            // locations are covered by exactly one brand; brand-less locations don't count.
            const brandIds = [...new Set((locationIds || []).map(id => locationsMap[id]?.brand?.id).filter(Boolean))];
            appearance = brandIds.length === 1 ? brands?.find(b => b.id === brandIds[0])?.appearance || null : null;
        }

        const businessAddress = location ? [location.addressLine1, location.city].filter(Boolean).join(', ') : '';
        const hasBusinessAddress = !!location?.hasBusinessAddress && !!businessAddress;

        return {
            menu: {
                names: document?.names || [],
                descriptions: document?.descriptions || [],
                currencyCode: currencyCode || 'USD',
                languages,
                locale: accountLocale,
                locationName: location ? location.locationName || location.internalName : null,
                hasBusinessAddress,
                businessAddress: hasBusinessAddress ? businessAddress : null,
                appearance,
                sections: (document?.sections || []).map(section => ({
                    id: section.id,
                    names: section.names,
                    descriptions: section.descriptions,
                    items: (section.items || []).map(item => ({
                        ...item,
                        priceAmount: toPreviewNumber(item.priceAmount),
                        options: (areOptionsEnabled(item) ? item.options || [] : [])
                            .filter(option => !option.isHidden)
                            .map(option => ({ ...option, priceAmount: toPreviewNumber(option.priceAmount) })),
                    })),
                })),
            },
            // Starting language only — the iframe's own selector takes over after load
            language: languages.includes(defaultLanguage) ? defaultLanguage : languages[0] || defaultLanguage,
        };
    };

    const sendPreviewData = () => {
        iframeRef.current?.contentWindow?.postMessage({ type: 'pl-preview-menu', data: getPreviewData() }, '*');
    };
    const sendPreviewDataRef = useRef(sendPreviewData);
    sendPreviewDataRef.current = sendPreviewData;

    useEffect(() => {
        const controller = new AbortController();
        const handleMessage = event => {
            if (event.origin === import.meta.env.REACT_APP_SURVEY_DOMAIN) {
                const message = event.data;
                if (message.type === 'pl-preview-iframe-loaded') {
                    setIsLoaded(true);
                    sendPreviewDataRef.current();
                }
            }
        };
        window.addEventListener('message', handleMessage, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    // Debounced: the builder rewrites the whole document per keystroke and the payload
    // is the whole menu, so per-keystroke sends would serialize it on every character.
    useEffect(() => {
        if (!isLoaded) return undefined;
        const timer = setTimeout(() => sendPreviewDataRef.current(), 200);
        return () => clearTimeout(timer);
    }, [isLoaded, document, currencyCode, locationIds, effectiveLocationId, brands, locationsMap, accountLocale]);

    return (
        <div>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px', textAlign: 'center' }}
            >
                <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                    <InternalLink
                        to={RoutePath.AccountSettingsBrands}
                        sx={{ color: 'var(--primary-main)', cursor: 'pointer', textDecoration: 'none' }}
                    >
                        {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                    </InternalLink>
                </Trans>
            </Typography>
            <div style={{ maxWidth: 375, margin: '0 auto' }}>
                {(locationIds || []).length > 1 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                        <AddDropdownHeader
                            options={locationIds.map(id => ({ value: id, label: getLocationLabel(id) }))}
                            onAdd={setSelectedLocationId}
                            buttonText={getLocationLabel(effectiveLocationId)}
                            buttonProps={{ size: 'small', sx: { backgroundColor: '#fff' } }}
                        />
                    </div>
                ) : null}
                {!isCreate ? <PublicLinkField url={publicLink} /> : null}
            </div>
            {!document?.sections?.length ? (
                <Typography
                    variant="body2"
                    align="center"
                    sx={{ color: 'var(--text-secondary)', marginBottom: '16px' }}
                >
                    {t('MenusPage.previewEmpty')}
                </Typography>
            ) : null}
            <PhonePreviewFrame>
                <iframe
                    ref={iframeRef}
                    title="menu-preview"
                    width="100%"
                    height="100%"
                    src={`${import.meta.env.REACT_APP_SURVEY_DOMAIN}/preview/menu`}
                    frameBorder="0"
                ></iframe>
            </PhonePreviewFrame>
        </div>
    );
};

export default Preview;
